import { Snackbar as FreddySnackbar, Loader, Box } from '@coolblue-development/freddy';
import { EmailTopPanel } from './TopPanel/EmailTopPanel';
import EmailMessageItem from './Body/EmailMessageItem';
import PreviousEmails from './PreviousEmails';
import { useEmailContext } from '../../../../contexts/EmailContext';
import ReplyContainer from './Reply/ReplyContainer';
import OutboundEmailComposer from './Outbound/OutboundEmailComposer';
import EmptyState from '../Common/EmptyState';
import { useSnackbar } from '../../../../utils/hooks/useSnackbar';

const EmailPage = (): JSX.Element => {
  const { emailThreads, selectedEmail, outboundContactId, isLoadingThread } = useEmailContext();
  const { snackbar, show, hideSnackbar } = useSnackbar();
  const emailThread = selectedEmail ? emailThreads[selectedEmail] : undefined;
  const isOutbound = selectedEmail !== null && selectedEmail === outboundContactId;
  const isSelectedThreadLoading = selectedEmail ? isLoadingThread(selectedEmail) : false;

  const messages = emailThread?.messages ?? [];
  // The allocated message isn't always the newest one - the customer can reply again before the
  // agent responds. Everything from the allocated message onward
  // needs to render directly rather than being treated as thread history.
  const activeIndex = messages.findIndex(message => message.contactId === selectedEmail);
  const boundaryIndex = activeIndex === -1 ? messages.length - 1 : activeIndex;

  const previousMessages = messages.slice(0, boundaryIndex);
  const currentMessages = messages.slice(boundaryIndex);
  const latestMessage = currentMessages.length > 0 ? currentMessages[currentMessages.length - 1] : null;

  const handleAttachmentDownloadError = (): void => show('Failed to download attachment. Please try again.', 'error');

  const renderBody = (): JSX.Element => {
    if (selectedEmail === null) {
      return <EmptyState title="No active email" />;
    }

    if (isSelectedThreadLoading) {
      return <Loader />;
    }

    return (
      <Box sx={{ overflowY: 'auto' }}>
        <PreviousEmails messages={previousMessages} onAttachmentDownloadError={handleAttachmentDownloadError} />

        {currentMessages
          .slice(0, -1)
          .map(
            (message, index) =>
              message.contentLoaded && (
                <EmailMessageItem
                  key={message.contactId}
                  message={message}
                  index={boundaryIndex + index}
                  onAttachmentDownloadError={handleAttachmentDownloadError}
                />
              )
          )}

        {latestMessage && latestMessage.contentLoaded && !(isOutbound && !latestMessage.bodyHtml) && (
          <EmailMessageItem
            key={latestMessage.contactId}
            message={latestMessage}
            index={messages.length - 1}
            initiallyCollapsed={false}
            onAttachmentDownloadError={handleAttachmentDownloadError}
          />
        )}
        {isOutbound ? (
          <OutboundEmailComposer
            onSendSuccess={() => show('Email sent successfully!', 'success')}
            onSendError={message => show(message, 'error')}
          />
        ) : (
          <ReplyContainer />
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        boxShadow: '0px 4px 16px 0px #246CB429',
        borderRadius: '12px',
        overflowY: 'auto',
        position: 'relative'
      }}
    >
      <EmailTopPanel />
      {renderBody()}

      <FreddySnackbar
        open={snackbar.open}
        title={snackbar.title}
        intent={snackbar.intent}
        duration={snackbar.duration}
        onClose={hideSnackbar}
        placement="bottom"
      />
    </Box>
  );
};

export default EmailPage;
